import useAuthStore from '@/store/authStore';
import { clearAllFromLocalstorage, removeAuthStorage } from '@/utils/localstorage';
import logger from '@/utils/logger';
import axios from 'axios';
import { toast } from 'react-toastify';

const imageAxios = axios.create({
  baseURL: import.meta.env.VITE_IMAGE_UPLOAD_URL,
});

const logout = () => {
  const clearAuth = useAuthStore.getState().clearAuth;
  removeAuthStorage();
  clearAuth();
  useAuthStore.persist.clearStorage();
  clearAllFromLocalstorage();
};

let retryCount = 0;
const MAX_RETRIES = 3;
let isLoggingOut = false;

// request interceptor (INSTANCE only)
imageAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token') ?? '';
  if (token) {
    config.headers['authorization'] = `Bearer ${token}`;
  }
  return config;
});

// response interceptor (INSTANCE only)
imageAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const isUnauthorized = error.response?.status === 401;

    if (isUnauthorized && retryCount < MAX_RETRIES) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          retryCount++;
          const refreshResponse = await axios.post(
            `${import.meta.env.VITE_API_BASE_URL}auth/refresh-token`,
            { refreshToken }
          );

          const { token, refreshToken: newRefreshToken } = refreshResponse.data?.data || {};

          if (token && newRefreshToken) {
            localStorage.setItem('access_token', token);
            localStorage.setItem('refresh_token', newRefreshToken);
            error.config.headers['authorization'] = `Bearer ${token}`;
            return imageAxios(error.config);
          }
        } catch (err) {
          logger.error(err);
          toast.error('Session expired. Please login again.');
          retryCount = 0;
          logout();
        }
      }
    }

    if (isUnauthorized && !isLoggingOut) {
      isLoggingOut = true;
      logout();
    }

    retryCount = 0;
    return Promise.reject(error);
  }
);

// Export methods
const httpsCallForImage = {
  get: imageAxios.get,
  post: imageAxios.post,
  put: imageAxios.put,
  delete: imageAxios.delete,
  patch: imageAxios.patch,
};

export default httpsCallForImage;
